// src/resources/dashboards/Painel360/PulseSection.tsx
import React from 'react';
import { Box, Typography } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import dayjs from 'dayjs';
import { Area, AreaChart, ResponsiveContainer, Tooltip } from 'recharts';
import { COLORS, DeltaChip, ErrorCard, SectionHeader, SkeletonCard, cardSx, fmtBrl, fmtNum, useCountUp } from './shared';
import { Kpi, OverviewData, SectionState } from './types';

// Tooltip do sparkline: dia + valor formatado.
const SparkTip = ({ active, payload, format }: any) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <Box sx={{ bgcolor: 'rgba(30,41,59,0.94)', color: '#fff', px: 1, py: 0.5, borderRadius: 1, boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
      <Typography sx={{ fontSize: 10.5, opacity: 0.8 }}>{dayjs(p.date).format('DD/MM')}</Typography>
      <Typography sx={{ fontSize: 12, fontWeight: 800 }}>{format(p.value)}</Typography>
    </Box>
  );
};

const Sparkline = ({ data, color, format }: { data: Kpi['spark']; color: string; format: (v: number) => string }) => (
  data.length > 1 ? (
    <Box sx={{ width: 120, height: 40, opacity: 0.95 }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 6, right: 0, bottom: 0, left: 0 }}>
          <Tooltip content={<SparkTip format={format} />} allowEscapeViewBox={{ x: true, y: true }} wrapperStyle={{ zIndex: 20 }} cursor={{ stroke: color, strokeWidth: 1, strokeDasharray: '3 3' }} />
          <Area type="monotone" dataKey="value" stroke={color} fill={color} fillOpacity={0.12} strokeWidth={2} isAnimationActive />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  ) : null
);

const KpiCard = ({ label, kpi, color, format }: {
  label: string; kpi: Kpi; color: string; format: (v: number) => string;
}) => {
  const animated = useCountUp(kpi.value);
  return (
    <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 120 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: COLORS.faint }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 26, fontWeight: 800, color: COLORS.ink, mt: 0.5 }}>{format(animated)}</Typography>
      {/* Delta à esquerda, sparkline à direita — linha no rodapé, sem sobrepor */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, mt: 'auto', pt: 1 }}>
        <DeltaChip deltaPct={kpi.deltaPct} />
        <Box sx={{ ml: 'auto' }}><Sparkline data={kpi.spark} color={color} format={format} /></Box>
      </Box>
    </Box>
  );
};

const PulseSection = ({ state }: { state: SectionState<OverviewData> }) => {
  if (state.loading) return <SkeletonCard height={130} />;
  if (state.error) return <ErrorCard onRetry={state.refetch} height={130} />;
  if (!state.data) return null;
  const { kpis } = state.data;

  return (
    <Box>
      <SectionHeader
        icon={<BoltIcon sx={{ fontSize: 18 }} />}
        gradient={`linear-gradient(135deg, ${COLORS.success}, ${COLORS.teal})`}
        title="Pulso do período"
        subtitle="Abastecimentos identificados pelo app · comparado com o período anterior"
      />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)', xl: 'repeat(6, 1fr)' }, gap: 1.75 }}>
        <KpiCard label="Faturamento identificado" kpi={kpis.revenue} color={COLORS.success} format={fmtBrl} />
        <KpiCard label="Volume" kpi={kpis.volume} color={COLORS.teal} format={v => `${fmtNum(v)} L`} />
        <KpiCard label="Abastecimentos" kpi={kpis.fillins} color={COLORS.blue} format={v => fmtNum(v)} />
        <KpiCard label="Ticket médio" kpi={kpis.avgTicket} color={COLORS.purple} format={v => fmtBrl(v)} />
        <KpiCard label="Clientes ativos" kpi={kpis.activeCustomers} color={COLORS.brand} format={v => fmtNum(v)} />
        <KpiCard label="Novos clientes" kpi={kpis.newCustomers} color={COLORS.warning} format={v => fmtNum(v)} />
      </Box>
    </Box>
  );
};

export default PulseSection;
